/**
 * Backup generator utilities.
 *
 * generateBackupFile: calls the backend /backup endpoint which enriches
 * actions with PD resource snapshots AND includes scan/discovery metadata
 * from every scan run during the session.
 *
 * For CSV format with multiple resource types the backend returns a ZIP
 * archive (format="zip"); this is handled transparently.
 *
 * generateBackupJson is kept for backwards compatibility but delegates
 * to generateBackupFile with format="json".
 */

import { appConfig } from '@/config/app-config';

export interface BackupResult {
  filename: string;
  blob: Blob;
  actionCount: number;
  format: string;
}

const MIME_BY_FORMAT: Record<string, string> = {
  json: 'application/json',
  csv: 'text/csv',
  zip: 'application/zip',
  terraform: 'text/plain',
};

export async function generateBackupFile(
  threadId: string,
  agentId: string,
  token: string,
  format: 'json' | 'csv' | 'terraform' = 'json',
): Promise<BackupResult> {
  const res = await fetch(
    `${appConfig.API_BASE_URL}/agents/${agentId}/threads/${threadId}/backup`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    },
  );
  if (!res.ok) throw new Error(`Backup request failed: ${res.status}`);

  const data = await res.json();

  if (!data.content_base64) throw new Error('Backup generation failed: empty content');

  const bytes = Uint8Array.from(atob(data.content_base64), (c) => c.charCodeAt(0));
  const effectiveFormat: string = data.format ?? format;
  const mime = MIME_BY_FORMAT[effectiveFormat] ?? 'application/octet-stream';
  const blob = new Blob([bytes], { type: mime });

  return {
    filename: data.filename ?? `ocu-backup.${effectiveFormat}`,
    blob,
    actionCount: data.action_count ?? 0,
    format: effectiveFormat,
  };
}

/** @deprecated Use generateBackupFile instead. */
export async function generateBackupJson(
  threadId: string,
  agentId: string,
  token: string,
): Promise<BackupResult> {
  return generateBackupFile(threadId, agentId, token, 'json');
}

export function triggerBlobDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
